/**
 * What each role in a cellar may do, as one rule both sides read.
 *
 * Here rather than in `access.ts` for the reason the drinking window is here:
 * the server decides what it will accept and the browser decides what it draws,
 * and those two answers must be the same answer. A button offered where the
 * route refuses is the *try again* that can never work; a button withheld where
 * the route would have allowed it is a feature nobody finds.
 *
 * Pure, so it is tested directly and costs the browser bundle nothing.
 *
 * ## Three levels, and each name says what it protects
 *
 * `read` — everybody with access. The cellar's wine: what is owned, where it
 * stands, what anybody thought of it.
 *
 * `carry` — carrying a bottle. Taking one out to a dinner, bringing it back,
 * undoing either, and asking what would suit what is being cooked. These move
 * a bottle about without deciding anything about the cellar's contents.
 *
 * `run` — the running of the cellar. Cataloguing a wine, buying a lot, moving
 * bottles between shelves, recording one as drunk, correcting a departure, the
 * vocabulary, the guest list, the export, and undo. Owner alone.
 *
 * **Drinking sits in `run` and not in `carry`**, which is the correction made
 * on 2026-09-21, the day after the middle role was first given to somebody.
 * The line drawn then — *these change bottles rather than the cellar* — put
 * drinking on the wrong side of itself: a bottle drunk is a bottle gone, and
 * what the cellar holds is the one thing the owner's record is for. Carrying
 * is reversible by walking back to the cabinet; drinking is not reversible at
 * all, and undoing it is a compensating event in a history that belongs to the
 * owner. So the husgäst takes the bottle to the dinner and the owner keeps the
 * books, which is what the two words already meant to the person using this.
 */
/**
 * The contributor, redefined on 2026-09-21 and narrowed again the same day.
 *
 * The role has sat in the enum since the first migration, reserved and
 * unbuilt, described as *full write, for a household member who also fills and
 * empties these cabinets*. Nothing was ever written with it, so the value was
 * free to mean what a real use needed — and the real use, when it arrived, was
 * narrower than the reservation twice over.
 *
 * **A husgäst is a guest who may carry a bottle out and ask what goes with the
 * lamb.** That is the whole of the difference, and it is deliberately stated
 * as a difference from the guest rather than as a list of its own: everything
 * a guest may do, plus taking a bottle out of the cabinet, bringing it back,
 * and *Till maten*. They do not record a bottle as drunk, they do not
 * catalogue wine, and they do not decide who else gets to look.
 *
 * Widening this later is a decision about one function. Having built the wide
 * version first and narrowing it would have been a decision about every
 * endpoint.
 */
export function mayCarry(role) {
    return role === "Owner" || role === "Contributor";
}
/**
 * Whether this role runs the cellar.
 *
 * Owner alone, which is what `write` has always meant in practice — the enum
 * had a third value and nobody held it. What changes is that the check now
 * says so rather than being true by accident, and that a contributor is
 * refused here rather than falling through the *not a guest* test that stood
 * in for this one.
 *
 * Recording a bottle as drunk is here too, since 2026-09-21. It is the one act
 * of the four on a bottle that cannot be walked back by doing something
 * ordinary afterwards, and it is the cellar's stock rather than the evening:
 * see the note at the top of this file.
 */
export function mayRunCellar(role) {
    return role === "Owner";
}
/**
 * **Prices are not decided here**, though they are a third thing a role
 * settles. `maySeePrices` stays in `backend/src/http/prices.ts`, where it has
 * a file of its own so it can be tested without building a Prisma client, and
 * where the browser has no use for it: the server resolves the answer and
 * sends `seesPrices` on the cellar summary, so no screen knows the rule and no
 * screen can get it wrong. Writing it a second time here would be the drift
 * that file was split out to prevent.
 */
//# sourceMappingURL=cellar-roles.js.map